/**
 * Normalizes SharePoint/OneDrive URLs to embed view format
 * @param {string} url - The SharePoint/OneDrive URL to normalize
 * @returns {string|null} - The normalized embed URL or null if invalid
 */
export function toEmbedUrl(url) {
  if (!url) return null;
  
  try {
    const u = new URL(url);
    
    // If it's already a proper SharePoint embed URL, return as-is
    if (u.pathname.includes("/_layouts/15/embed.aspx")) {
      return u.toString();
    }
    
    // If it's already an embed link, return as-is
    if (u.searchParams.get("action") === "embedview") {
      return u.toString();
    }

    // Known viewer pages can switch to embed by setting action=embedview
    // e.g. .../_layouts/15/Doc.aspx?sourcedoc=...&action=embedview
    if (u.pathname.includes("/_layouts/15/Doc.aspx")) {
      u.searchParams.set("action", "embedview");
      return u.toString();
    }

    // For OneDrive sharing links, try to convert to embed format
    if (u.hostname.includes("sharepoint.com") || u.hostname.includes("sharepoint-df.com")) {
      // Check if it's a sharing link that can be converted
      if (u.pathname.includes("/s/") || u.searchParams.has("id") || u.searchParams.has("resid")) {
        // Try to construct embed URL
        const baseUrl = `${u.protocol}//${u.hostname}`;
        const pathParts = u.pathname.split('/');
        const siteIndex = pathParts.findIndex(part => part === 'sites' || part === 'teams');
        
        if (siteIndex !== -1 && pathParts[siteIndex + 1]) {
          const siteName = pathParts[siteIndex + 1];
          const docId = u.searchParams.get('id') || u.searchParams.get('resid');
          
          if (docId) {
            // Construct embed URL
            const embedUrl = `${baseUrl}/sites/${siteName}/_layouts/15/Doc.aspx?sourcedoc=${docId}&action=embedview`;
            return embedUrl;
          }
        }
      }
    }

    // If it's a share link that resolves to a viewer, keep as-is
    // The iFrame host must allow embedding
    return url;
    
  } catch (error) {
    console.warn('Failed to normalize SharePoint URL:', error);
    return url; // Return original URL as fallback
  }
}

/**
 * Validates if a URL is a valid SharePoint/OneDrive URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid SharePoint/OneDrive URL
 */
export function isValidSharePointUrl(url) {
  if (!url) return false;
  
  try {
    const u = new URL(url);
    return u.hostname.includes('sharepoint.com') || 
           u.hostname.includes('sharepoint-df.com') ||
           u.hostname.includes('onedrive.live.com') ||
           u.hostname.includes('1drv.ms');
  } catch {
    return false;
  }
}

/**
 * Extracts video ID from SharePoint URL for localStorage key generation
 * @param {string} url - The SharePoint URL
 * @returns {string} - The video ID or a hash of the URL
 */
export function getVideoId(url) {
  if (!url) return 'unknown';
  
  try {
    const u = new URL(url);
    
    // For SharePoint embed URLs, extract UniqueId
    const uniqueId = u.searchParams.get('UniqueId');
    if (uniqueId) return uniqueId;
    
    // For other SharePoint URLs, try to get document ID
    const docId = u.searchParams.get('id') || u.searchParams.get('resid');
    if (docId) return docId;
    
    // Fallback to pathname hash
    return btoa(u.pathname).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  } catch {
    return btoa(url).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  }
}
